import { useCallback, useState } from 'react'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { Button } from 'primereact/button'
import { Combobox } from 'medere-ui-framework'
import { searchDeterminations } from '../../../services/StudyResultsController'
import type { DeterminationOption } from '../../../services/StudyResultsController'
import { StudyInformDeterminationDTO } from '../../../dto/StudiesInformListItemDTO'

interface DeterminationsCardProps {
  determinations: StudyInformDeterminationDTO[]
  onChange: (determinations: StudyInformDeterminationDTO[]) => void
  isReadOnly?: boolean
}

export function DeterminationsCard({ determinations, onChange, isReadOnly }: DeterminationsCardProps) {
  const [selectedDetermination, setSelectedDetermination] = useState<DeterminationOption | null>(null)
  const [valueInput, setValueInput] = useState<string>('')
  const [numericValueInput, setNumericValueInput] = useState<number | null>(null)

  const handleDeterminationSearch = useCallback(
    (props: { filterValue?: string }) => searchDeterminations(props.filterValue ?? '').then((items) => items ?? []),
    [],
  )

  const resetAddRow = () => {
    setSelectedDetermination(null)
    setValueInput('')
    setNumericValueInput(null)
  }

  const handleAdd = useCallback(() => {
    if (!selectedDetermination) return

    const duplicate = determinations.some((d) => d.determinationId === selectedDetermination.id)
    if (duplicate) return

    const resolvedValue = selectedDetermination.numeric
      ? numericValueInput != null ? String(numericValueInput) : ''
      : valueInput

    if (!resolvedValue.trim()) return

    onChange([
      ...determinations,
      {
        determinationId: selectedDetermination.id,
        description: selectedDetermination.description,
        numeric: selectedDetermination.numeric,
        unit: selectedDetermination.unit,
        value: resolvedValue,
      },
    ])

    resetAddRow()
  }, [selectedDetermination, valueInput, numericValueInput, determinations, onChange])

  const handleRemove = useCallback(
    (determinationId: number) => {
      onChange(determinations.filter((d) => d.determinationId !== determinationId))
    },
    [determinations, onChange],
  )

  return (
    <div className='study-inform-card mb-3 studies-inform-determinations-card'>
      <div className='study-inform-card-header'>
        <span className='studies-inform-section-title'>Determinaciones</span>
      </div>

      <div className='determinations-table'>
        <div className='determinations-table__head'>
          <span>Detalle</span>
          <span>Valor numérico</span>
          <span>Unidad</span>
          <span />
        </div>

        {determinations.map((row) => (
          <div className='determinations-table__row' key={row.determinationId}>
            <span className='determinations-table__cell'>{row.description}</span>
            <span className='determinations-table__cell'>{row.value}</span>
            <span className='determinations-table__cell'>{row.unit ?? ''}</span>
            <span className='determinations-table__cell determinations-table__cell--action'>
              {!isReadOnly && (
                <Button
                  icon='pi pi-trash'
                  className='p-button-text p-button-rounded p-button-sm determinations-table__remove-btn'
                  onClick={() => handleRemove(row.determinationId)}
                  aria-label='Eliminar determinación'
                />
              )}
            </span>
          </div>
        ))}

        {!isReadOnly && (
          <div className='determinations-table__row determinations-table__row--add'>
            <Combobox
              value={selectedDetermination}
              setValue={(val: DeterminationOption | null) => {
                setSelectedDetermination(val)
                setValueInput('')
                setNumericValueInput(null)
              }}
              getItems={handleDeterminationSearch}
              optionLabel='description'
              placeholder='Seleccionar'
              className='determinations-table__combobox'
            />
            <span className='determinations-table__cell'>
              {selectedDetermination?.numeric === false ? (
                <InputText
                  value={valueInput}
                  onChange={(e) => setValueInput(e.target.value)}
                  placeholder='...'
                  className='determinations-table__input'
                />
              ) : (
                <InputNumber
                  value={numericValueInput}
                  onValueChange={(e) => setNumericValueInput(e.value ?? null)}
                  placeholder='...'
                  className='determinations-table__input'
                  minFractionDigits={0}
                  maxFractionDigits={4}
                  locale='es-AR'
                />
              )}
            </span>
            <span className='determinations-table__cell'>{selectedDetermination?.unit ?? '...'}</span>
            <span className='determinations-table__cell determinations-table__cell--action'>
              <Button
                icon='pi pi-plus'
                className='p-button-rounded p-button-text determinations-table__add-btn'
                onClick={handleAdd}
                disabled={!selectedDetermination}
                aria-label='Agregar determinación'
              />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
